import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import './BuildingDetails.css';

interface Building {
  id: number;
  name: string;
  address: string;
  description?: string;
  manager_name?: string;
  manager_phone?: string;
  manager_email?: string;
  total_floors: number;
  flats_per_floor: number;
  total_flats: number;
  status: 'active' | 'inactive' | 'maintenance';
  amenities?: string[];
  rules?: string[];
  maintenance_fee: number;
  image_url?: string;
  created_at: string;
  updated_at: string;
  statistics: {
    total_flats: number;
    occupied_flats: number;
    available_flats: number;
    maintenance_flats: number;
    reserved_flats: number;
    occupancy_rate: number;
    total_floors: number;
    committee_members: number;
    upcoming_maintenance: number;
    overdue_maintenance: number;
  };
  flats: Flat[];
  buildingCommittees: BuildingCommittee[];
  maintenance: BuildingMaintenance[];
}

interface Owner {
  id: number;
  user_id: number;
  name: string;
  phone: string;
  email: string;
  user_type: 'owner' | 'family_member' | 'tenant' | 'guest';
  ownership_type: string;
  ownership_percentage: number;
  is_primary_owner: boolean;
  purchase_date?: string;
  purchase_price?: number;
  current_value?: number;
}

interface Flat {
  id: number;
  floor_number: number;
  flat_letter: string;
  flat_number: string;
  area?: number;
  bedrooms: number;
  bathrooms: number;
  flat_type: '1BHK' | '2BHK' | '3BHK' | '4BHK' | 'Penthouse';
  status: 'available' | 'occupied' | 'maintenance' | 'reserved';
  owner_name?: string;
  owner_phone?: string;
  owner_email?: string;
  owner_user_id?: number;
  owner_user_type?: 'owner' | 'family_member' | 'tenant' | 'guest';
  all_owners?: Owner[];
  purchase_date?: string;
  purchase_price?: number;
  current_value?: number;
  amenities?: string[];
  notes?: string;
}

interface BuildingCommittee {
  id: number;
  position: string;
  member_name: string;
  member_phone: string;
  member_email: string;
  flat_number: string;
  elected_date: string;
  term_end_date: string;
  status: 'active' | 'inactive' | 'resigned';
  responsibilities?: string;
}

interface BuildingMaintenance {
  id: number;
  title: string;
  description: string;
  type: 'routine' | 'emergency' | 'repair' | 'renovation' | 'cleaning';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  scheduled_date: string;
  scheduled_time?: string;
  completed_date?: string;
  estimated_cost?: number;
  actual_cost?: number;
  contractor_name?: string;
  contractor_phone?: string;
  notes?: string;
  affected_flats?: string[];
  created_at: string;
}

interface BuildingDetailsProps {
  buildingId: number;
  onBack: () => void;
}

const BuildingDetails: React.FC<BuildingDetailsProps> = ({ buildingId, onBack }) => {
  const [building, setBuilding] = useState<Building | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'floors' | 'residents' | 'committee' | 'maintenance'>('overview');
  const [selectedFloor, setSelectedFloor] = useState<number | null>(null);
  const [editingOwner, setEditingOwner] = useState<number | null>(null);
  const [ownerFormData, setOwnerFormData] = useState({
    name: '',
    phone: '',
    email: '',
    user_type: 'owner' as 'owner' | 'family_member' | 'tenant' | 'guest'
  });

  // Owner Assignment State
  const [showOwnerAssignment, setShowOwnerAssignment] = useState(false);
  const [selectedFlat, setSelectedFlat] = useState<Flat | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [assignmentForm, setAssignmentForm] = useState({
    user_id: null as number | null,
    ownership_type: 'owner' as 'owner' | 'co_owner' | 'legal_heir',
    ownership_percentage: 100,
    is_primary_owner: true
  });
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    fetchBuildingDetails();
  }, [buildingId]);


  const fetchBuildingDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/buildings/${buildingId}`);
      
      if (response.data.success) {
        setBuilding(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch building details');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error fetching building details');
      console.error('Error fetching building details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'maintenance': return 'warning';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return '✅';
      case 'inactive': return '⏸️';
      case 'maintenance': return '🔧';
      default: return '❓';
    }
  };

  const getFlatStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'occupied': return 'primary';
      case 'maintenance': return 'warning';
      case 'reserved': return 'info';
      default: return 'secondary';
    }
  };

  const getFlatTypeColor = (type: string) => {
    switch (type) {
      case '1BHK': return 'info';
      case '2BHK': return 'success';
      case '3BHK': return 'warning';
      case '4BHK': return 'danger';
      case 'Penthouse': return 'dark';
      default: return 'secondary';
    }
  };

  const handleEditOwner = (flat: Flat) => {
    setEditingOwner(flat.id);
    setOwnerFormData({
      name: flat.owner_name || '',
      phone: flat.owner_phone || '',
      email: flat.owner_email || '',
      user_type: flat.owner_user_type || 'owner'
    });
  };

  const handleCancelEdit = () => {
    setEditingOwner(null);
    setOwnerFormData({
      name: '',
      phone: '',
      email: '',
      user_type: 'owner'
    });
  };

  const handleSaveOwner = async (flatId: number) => {
    try {
      // Find the flat to get the user_id
      const flat = building?.flats.find(f => f.id === flatId);
      if (!flat || !flat.owner_user_id) {
        throw new Error('Owner user ID not found');
      }

      // Update the user information
      await api.put(`/users/${flat.owner_user_id}`, {
        name: ownerFormData.name,
        phone: ownerFormData.phone,
        email: ownerFormData.email,
        user_type: ownerFormData.user_type
      });

      // Refresh building details
      await fetchBuildingDetails();
      setEditingOwner(null);
      setOwnerFormData({
        name: '',
        phone: '',
        email: '',
        user_type: 'owner'
      });
    } catch (error) {
      console.error('Error updating owner:', error);
      alert('Failed to update owner information');
    }
  };

  const handleOwnerFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setOwnerFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Owner Assignment Functions
  const handleAssignOwner = (flat: Flat) => {
    setSelectedFlat(flat);
    setShowOwnerAssignment(true);
    fetchUsers({
      building_id: building?.id || null,
      flat_id: flat?.id || null
    });
  };

  const handleViewOwners = (flat: Flat) => {
    setSelectedFlat(flat);
    // Show owners in a modal or expand the flat card
    console.log('View owners for flat:', flat.flat_number || 'Unknown', flat.all_owners);
  };

  
  const fetchUsers = async (params?: { building_id?: number | null; floor_id?: number | null; flat_id?: number | null }) => {
    try {
      const searchParams = new URLSearchParams();
      searchParams.append('per_page', '100');
      if (params?.building_id) searchParams.append('building_id', String(params.building_id));
      if (params?.floor_id) searchParams.append('floor_id', String(params.floor_id));
      if (params?.flat_id) searchParams.append('flat_id', String(params.flat_id));

      const response = await api.get(`/users?${searchParams.toString()}`);
      if (response.data.success) {
        setUsers(response.data.data.data || response.data.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  

  // const fetchUsers = async () => {
  //   try {
  //     setLoadingUsers(true);
  //     const response = await api.get('/users?per_page=100');
  //     if (response.data.success) {
  //       setUsers(response.data.data.data || response.data.data);
  //     }
  //   } catch (err) {
  //     console.error('Error fetching users:', err);
  //   } finally {
  //     setLoadingUsers(false);
  //   }
  // };

  const handleAssignmentFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setAssignmentForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              type === 'number' ? parseFloat(value) : value
    }));
  };

  const handleAssignOwnerSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFlat || !assignmentForm.user_id) return;

    try {
      setLoading(true);
      
      // First, create or update the flat if it doesn't exist
      let flatId = selectedFlat.id;
      if (flatId === 0) {
        try {
          // Create the flat first
          const flatResponse = await api.post('/flats', {
            building_id: building?.id,
            floor_number: parseInt(selectedFlat.flat_number.split('-')[0]),
            flat_letter: selectedFlat.flat_number.split('-')[1],
            flat_number: selectedFlat.flat_number,
            status: 'occupied',
            bedrooms: 2, // Default values
            bathrooms: 1,
            flat_type: '2BHK'
          });
          
          if (flatResponse.data.success) {
            flatId = flatResponse.data.data.id;
          } else {
            throw new Error(flatResponse.data.message || 'Failed to create flat');
          }
        } catch (flatError: any) {
          console.error('Error creating flat:', flatError);
          setError(flatError.response?.data?.message || 'Error creating flat: ' + flatError.message);
          return;
        }
      }

      // Then assign the owner
      const response = await api.post('/owners', {
        user_id: assignmentForm.user_id,
        building_id: building?.id,
        flat_id: flatId,
        flat_number: selectedFlat.flat_number,
        ownership_type: assignmentForm.ownership_type,
        ownership_percentage: assignmentForm.ownership_percentage,
        is_primary_owner: assignmentForm.is_primary_owner,
        is_active: true,
        // If there is already a primary owner for this flat, allow admin to replace
        ...(selectedFlat?.owner_user_id ? { replace_primary: true } : {})
      });

      if (response.data.success) {
        setSuccess('Owner assigned successfully');
        await fetchBuildingDetails(); // Refresh building data
        setShowOwnerAssignment(false);
        setSelectedFlat(null);
        setAssignmentForm({
          user_id: null,
          ownership_type: 'owner',
          ownership_percentage: 100,
          is_primary_owner: true
        });
      } else {
        setError(response.data.message || 'Failed to assign owner');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error assigning owner');
      console.error('Error assigning owner:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users;

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      case 'urgent': return 'dark';
      default: return 'secondary';
    }
  };

  const getMaintenanceStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'secondary';
      default: return 'secondary';
    }
  };

  const getFlatsByFloor = (floorNumber: number) => {
    return building?.flats.filter(flat => flat.floor_number === floorNumber) || [];
  };

  const getFloorStats = (floorNumber: number) => {
    const flats = getFlatsByFloor(floorNumber);
    return {
      total: flats.length,
      occupied: flats.filter(f => f.status === 'occupied').length,
      available: flats.filter(f => f.status === 'available').length,
      maintenance: flats.filter(f => f.status === 'maintenance').length,
      reserved: flats.filter(f => f.status === 'reserved').length,
    };
  };

  if (loading) {
    return (
      <div className="building-details-loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading Building Details...</h3>
          <p>Please wait while we fetch the building information</p>
        </div>
      </div>
    );
  }

  if (error || !building) {
    return (
      <div className="building-details-error">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Unable to Load Building Details</h3>
          <p>{error || 'Building not found'}</p>
          <button className="btn btn-primary" onClick={onBack}>
            Back to Buildings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="building-details">
      {/* Header */}
      <div className="building-details-header">
        <div className="header-content">
          <button className="back-btn" onClick={onBack}>
            ← Back to Buildings
          </button>
          <div className="building-title">
            <h1>{building.name}</h1>
            <span className={`status-badge ${getStatusColor(building.status)}`}>
              {getStatusIcon(building.status)} {building.status}
            </span>
          </div>
          <p className="building-address">{building.address}</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary">Edit Building</button>
          <button className="btn btn-primary">Add Maintenance</button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="building-stats-cards">
        <div className="stat-card">
          <div className="stat-icon">🏠</div>
          <div className="stat-content">
            <h3>{building.statistics.total_flats}</h3>
            <p>Total Flats</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-content">
            <h3>{building.statistics.occupied_flats}</h3>
            <p>Occupied</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🆓</div>
          <div className="stat-content">
            <h3>{building.statistics.available_flats}</h3>
            <p>Available</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{building.statistics.occupancy_rate}%</h3>
            <p>Occupancy Rate</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔧</div>
          <div className="stat-content">
            <h3>{building.statistics.upcoming_maintenance}</h3>
            <p>Upcoming Maintenance</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⚠️</div>
          <div className="stat-content">
            <h3>{building.statistics.overdue_maintenance}</h3>
            <p>Overdue Maintenance</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="building-details-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📋 Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'floors' ? 'active' : ''}`}
          onClick={() => setActiveTab('floors')}
        >
          🏢 Floors & Flats
        </button>
        <button
          className={`tab-btn ${activeTab === 'residents' ? 'active' : ''}`}
          onClick={() => setActiveTab('residents')}
        >
          👥 Residents
        </button>
        <button
          className={`tab-btn ${activeTab === 'committee' ? 'active' : ''}`}
          onClick={() => setActiveTab('committee')}
        >
          🏛️ Committee
        </button>
        <button
          className={`tab-btn ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
        >
          🔧 Maintenance
        </button>
      </div>

      {/* Tab Content */}
      <div className="building-details-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              <div className="overview-section">
                <h3>Building Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Building Name:</label>
                    <span>{building.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Address:</label>
                    <span>{building.address}</span>
                  </div>
                  <div className="info-item">
                    <label>Total Floors:</label>
                    <span>{building.total_floors}</span>
                  </div>
                  <div className="info-item">
                    <label>Flats per Floor:</label>
                    <span>{building.flats_per_floor}</span>
                  </div>
                  <div className="info-item">
                    <label>Total Flats:</label>
                    <span>{building.total_flats}</span>
                  </div>
                  <div className="info-item">
                    <label>Maintenance Fee:</label>
                    <span>৳{building.maintenance_fee.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="overview-section">
                <h3>Manager Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Manager Name:</label>
                    <span>{building.manager_name || 'Not assigned'}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{building.manager_phone || 'Not provided'}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{building.manager_email || 'Not provided'}</span>
                  </div>
                </div>
              </div>

              <div className="overview-section">
                <h3>Amenities</h3>
                <div className="amenities-list">
                  {building.amenities?.map((amenity, index) => (
                    <span key={index} className="amenity-tag">
                      {amenity.replace('_', ' ').toUpperCase()}
                    </span>
                  )) || <p>No amenities listed</p>}
                </div>
              </div>

              <div className="overview-section">
                <h3>Building Rules</h3>
                <div className="rules-list">
                  {building.rules?.map((rule, index) => (
                    <div key={index} className="rule-item">
                      • {rule}
                    </div>
                  )) || <p>No rules listed</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'floors' && (
          <div className="floors-tab">
            <div className="floors-header">
              <h3>Floors & Flats Overview</h3>
              <div className="floor-filter">
                <select
                  value={selectedFloor || ''}
                  onChange={(e) => setSelectedFloor(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">All Floors</option>
                  {Array.from({ length: building.total_floors }, (_, i) => i + 1).map(floorNum => (
                    <option key={floorNum} value={floorNum}>
                      Floor {floorNum}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="floors-grid">
              {Array.from({ length: building.total_floors }, (_, i) => {
                const floorNumber = building.total_floors - i; // Show from top to bottom
                const floorStats = getFloorStats(floorNumber);
                
                if (selectedFloor && selectedFloor !== floorNumber) return null;

                return (
                  <div key={floorNumber} className="floor-card">
                    <div className="floor-header">
                      <h4>Floor {floorNumber}</h4>
                      <div className="floor-stats">
                        <span className="stat occupied">{floorStats.occupied}</span>
                        <span className="stat available">{floorStats.available}</span>
                        <span className="stat maintenance">{floorStats.maintenance}</span>
                      </div>
                    </div>
                    
                    <div className="flats-grid">
                      {Array.from({ length: building.flats_per_floor }, (_, flatIndex) => {
                        const flatLetter = String.fromCharCode(65 + flatIndex); // A, B, C, D, E, F, G, H
                        const flatNumber = `${floorNumber}-${flatLetter}`;
                        const flat = building.flats.find(f => f.flat_number === flatNumber);
                        
                        return (
                          <div key={flatIndex} className={`flat-card ${flat?.status || 'available'}`}>
                            <div className="flat-header">
                              <span className="flat-number">{flatNumber}</span>
                              <div className="flat-actions">
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleAssignOwner(flat || { id: 0, flat_number: flatNumber, status: 'available' } as Flat)}
                                  title="Assign Owner"
                                >
                                  <i className="fas fa-user-plus"></i>
                                </button>
                              </div>
                            </div>
                            <div className="flat-details">
                              <div className="flat-owner-info">
                                {flat && flat.all_owners && flat.all_owners.length > 0 ? (
                                  <div className="owner-details">
                                    <div className="owner-name">{flat.all_owners[0].name || 'Unknown'}</div>
                                    <div className="owner-phone">{flat.all_owners[0].phone || 'N/A'}</div>
                                    <div className="owner-type">{(flat.all_owners[0].user_type || 'owner').replace('_', ' ')}</div>
                                  </div>
                                ) : (
                                  <div className="no-owner">
                                    <span className="no-owner-text">No Owner</span>
                                    <button 
                                      className="assign-btn"
                                      onClick={() => handleAssignOwner(flat || { id: 0, flat_number: flatNumber, status: 'available' } as Flat)}
                                    >
                                      Assign Owner
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'residents' && (
          <div className="residents-tab">
            <h3>Residents & Flat Owners</h3>
            <div className="residents-grid">
              {building.flats
                .filter(flat => (flat.status || 'available') === 'occupied')
                .map((flat) => (
                  <div key={flat.id} className="resident-card">
                    <div className="resident-header">
                      <h4>{flat.flat_number || 'Unknown'}</h4>
                      <span className={`flat-type ${getFlatTypeColor(flat.flat_type || '2BHK')}`}>
                        {flat.flat_type || '2BHK'}
                      </span>
                      {flat.owner_user_id && (
                        <button 
                          className="edit-owner-btn"
                          onClick={() => handleEditOwner(flat)}
                          title="Edit Owner Information"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                      )}
                    </div>
                    
                    {editingOwner === flat.id ? (
                      <div className="owner-edit-form">
                        <div className="form-group">
                          <label>Name:</label>
                          <input
                            type="text"
                            name="name"
                            value={ownerFormData.name}
                            onChange={handleOwnerFormChange}
                            placeholder="Owner name"
                          />
                        </div>
                        <div className="form-group">
                          <label>Phone:</label>
                          <input
                            type="tel"
                            name="phone"
                            value={ownerFormData.phone}
                            onChange={handleOwnerFormChange}
                            placeholder="Phone number"
                          />
                        </div>
                        <div className="form-group">
                          <label>Email:</label>
                          <input
                            type="email"
                            name="email"
                            value={ownerFormData.email}
                            onChange={handleOwnerFormChange}
                            placeholder="Email address"
                          />
                        </div>
                        <div className="form-group">
                          <label>User Type:</label>
                          <select
                            name="user_type"
                            value={ownerFormData.user_type}
                            onChange={handleOwnerFormChange}
                          >
                            <option value="owner">Owner</option>
                            <option value="family_member">Family Member</option>
                            <option value="tenant">Tenant</option>
                            <option value="guest">Guest</option>
                          </select>
                        </div>
                        <div className="form-actions">
                          <button 
                            className="save-btn"
                            onClick={() => handleSaveOwner(flat.id)}
                          >
                            <i className="fas fa-save"></i> Save
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={handleCancelEdit}
                          >
                            <i className="fas fa-times"></i> Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="resident-info">
                        {flat.all_owners && flat.all_owners.length > 0 ? (
                          <div className="owners-list">
                            <h5>All Owners ({flat.all_owners.length})</h5>
                            {flat.all_owners.map((owner, index) => (
                              <div key={owner.id} className="owner-item">
                                <div className="owner-header">
                                  <span className="owner-name">{owner.name}</span>
                                  {owner.is_primary_owner && (
                                    <span className="primary-badge">Primary</span>
                                  )}
                                  <span className={`user-type ${owner.user_type}`}>
                                    {owner.user_type}
                                  </span>
                                </div>
                                <div className="owner-details">
                                  <div className="detail-row">
                                    <span className="label">Phone:</span>
                                    <span className="value">{owner.phone}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span className="label">Email:</span>
                                    <span className="value">{owner.email}</span>
                                  </div>
                                  <div className="detail-row">
                                    <span className="label">Ownership:</span>
                                    <span className="value">
                                      {owner.ownership_percentage}% ({owner.ownership_type})
                                    </span>
                                  </div>
                                  {owner.purchase_date && (
                                    <div className="detail-row">
                                      <span className="label">Purchase Date:</span>
                                      <span className="value">
                                        {new Date(owner.purchase_date).toLocaleDateString()}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                <div className="owner-actions">
                                  <button 
                                    className="edit-owner-btn-small"
                                    onClick={() => handleEditOwner(flat)}
                                    title="Edit Owner Information"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="no-owners">
                            <p>No owners assigned to this flat</p>
                            <button 
                              className="assign-owner-btn"
                              onClick={() => handleEditOwner(flat)}
                            >
                              <i className="fas fa-user-plus"></i> Assign Owner
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}

        {activeTab === 'committee' && (
          <div className="committee-tab">
            <h3>Building Committee</h3>
            <div className="committee-grid">
              {building.buildingCommittees.map((member) => (
                <div key={member.id} className="committee-card">
                  <div className="member-header">
                    <h4>{member.member_name}</h4>
                    <span className={`position ${member.position.toLowerCase()}`}>
                      {member.position}
                    </span>
                  </div>
                  <div className="member-info">
                    <div className="info-item">
                      <label>Flat:</label>
                      <span>{member.flat_number}</span>
                    </div>
                    <div className="info-item">
                      <label>Phone:</label>
                      <span>{member.member_phone}</span>
                    </div>
                    <div className="info-item">
                      <label>Email:</label>
                      <span>{member.member_email}</span>
                    </div>
                    <div className="info-item">
                      <label>Term:</label>
                      <span>
                        {new Date(member.elected_date).toLocaleDateString()} - {' '}
                        {new Date(member.term_end_date).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="info-item">
                      <label>Status:</label>
                      <span className={`status ${member.status}`}>
                        {member.status}
                      </span>
                    </div>
                    {member.responsibilities && (
                      <div className="info-item">
                        <label>Responsibilities:</label>
                        <span>{member.responsibilities}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="maintenance-tab">
            <h3>Maintenance Records</h3>
            <div className="maintenance-list">
              {building.maintenance.map((item) => (
                <div key={item.id} className="maintenance-card">
                  <div className="maintenance-header">
                    <h4>{item.title}</h4>
                    <div className="maintenance-badges">
                      <span className={`type ${item.type}`}>
                        {item.type.toUpperCase()}
                      </span>
                      <span className={`priority ${getPriorityColor(item.priority)}`}>
                        {item.priority.toUpperCase()}
                      </span>
                      <span className={`status ${getMaintenanceStatusColor(item.status)}`}>
                        {item.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="maintenance-content">
                    <p>{item.description}</p>
                    <div className="maintenance-details">
                      <div className="detail-item">
                        <label>Scheduled Date:</label>
                        <span>{new Date(item.scheduled_date).toLocaleDateString()}</span>
                      </div>
                      {item.scheduled_time && (
                        <div className="detail-item">
                          <label>Time:</label>
                          <span>{item.scheduled_time}</span>
                        </div>
                      )}
                      {item.contractor_name && (
                        <div className="detail-item">
                          <label>Contractor:</label>
                          <span>{item.contractor_name}</span>
                        </div>
                      )}
                      {item.estimated_cost && (
                        <div className="detail-item">
                          <label>Estimated Cost:</label>
                          <span>৳{item.estimated_cost.toLocaleString()}</span>
                        </div>
                      )}
                      {item.actual_cost && (
                        <div className="detail-item">
                          <label>Actual Cost:</label>
                          <span>৳{item.actual_cost.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    {item.notes && (
                      <div className="maintenance-notes">
                        <label>Notes:</label>
                        <p>{item.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Owner Assignment Modal */}
      {showOwnerAssignment && selectedFlat && (
        <div className="modal-overlay">
          <div className="modal-content owner-assignment-modal">
            <div className="modal-header">
              <h3>Assign Owner to Flat {selectedFlat.flat_number}</h3>
              <button
                className="modal-close"
                onClick={() => setShowOwnerAssignment(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleAssignOwnerSubmit} className="assignment-form">
              <div className="form-group">
                <label>Select User *</label>
                <select
                  name="user_id"
                  value={assignmentForm.user_id || ''}
                  onChange={handleAssignmentFormChange}
                  required
                  className="form-control"
                  disabled={loadingUsers}
                >
                  <option value="">Select a user...</option>
                  {filteredUsers.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} - {user.email}
                    </option>
                  ))}
                </select>
                {loadingUsers && <small className="text-info">Loading users...</small>}
              </div>

              <div className="form-group">
                <label>Ownership Type</label>
                <select
                  name="ownership_type"
                  value={assignmentForm.ownership_type}
                  onChange={handleAssignmentFormChange}
                  className="form-control"
                >
                  <option value="owner">Owner</option>
                  <option value="co_owner">Co-Owner</option>
                  <option value="legal_heir">Legal Heir</option>
                </select>
              </div>

              <div className="form-group">
                <label>Ownership Percentage (%)</label>
                <input
                  type="number"
                  name="ownership_percentage"
                  value={assignmentForm.ownership_percentage}
                  onChange={handleAssignmentFormChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="is_primary_owner"
                    checked={assignmentForm.is_primary_owner}
                    onChange={handleAssignmentFormChange}
                  />
                  <span className="checkmark"></span>
                  Primary Owner
                </label>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowOwnerAssignment(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !assignmentForm.user_id}
                >
                  {loading ? 'Assigning...' : 'Assign Owner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildingDetails;
